/**
 * Reusable Draft / Published / Private / Archived control for any content type.
 * Backend: PUT /api/admin/content/{contentType}/{itemId}/status
 * contentType: course | lab | lab_v2 | masterclass | ebook | pathway | external_module | external_course | programme | quiz
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

export const STATUS_STYLES = {
  draft: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  published: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  private: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  archived: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
};

/** Resolve the unified status from status / is_published (mirrors backend effective_status). */
export function resolveStatus(item, publishedValue = 'published') {
  const s = (item?.status || '').toLowerCase();
  if (s === 'draft' || s === 'archived' || s === 'private') return s;
  if (s === 'published' || s === publishedValue) return 'published';
  if (item && 'is_published' in item) return item.is_published ? 'published' : 'draft';
  if (!s) return 'published';
  return 'draft';
}

export function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLES[status] || STATUS_STYLES.draft}`} data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
}

export default function ContentStatusControl({ token, contentType, itemId, item, publishedValue, onChange, size = 'sm' }) {
  const [status, setStatus] = useState(resolveStatus(item, publishedValue));
  const [saving, setSaving] = useState(false);

  const change = async (next) => {
    if (next === status) return;
    if (next !== 'published' && !window.confirm(`Set to ${next}? ${next === 'private' ? 'Only admins will be able to see this item.' : 'Learners will no longer see this item.'} Enrolled learners keep their progress.`)) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${contentType}/${itemId}/status`, { status: next }, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(next);
      toast.success(`Status set to ${next}`);
      onChange && onChange(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <select
      value={status}
      disabled={saving}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => change(e.target.value)}
      className={`rounded-md border bg-zinc-900 text-xs capitalize px-2 ${size === 'sm' ? 'h-7' : 'h-9'} ${STATUS_STYLES[status] || ''}`}
      data-testid={`status-select-${contentType}-${itemId}`}
    >
      <option value="draft">Draft</option>
      <option value="published">Published</option>
      <option value="private">Private</option>
      <option value="archived">Archived</option>
    </select>
  );
}
